import { useState } from "react";
import { Header } from "@/components/Header";
import { StaticRouteBreadcrumbs } from "@/components/StaticRouteBreadcrumbs";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle } from "lucide-react";
import { getSupabase } from "@/integrations/supabase/lazy";
import { toast } from "sonner";
import { trackLead } from "@/lib/analytics";
import designServices from "@/assets/design-services.webp";

import { routeHead } from "@/lib/routeHead";

// Title/description come from scripts/routes.mjs, like the prerendered head.
const head = routeHead("/design-services");

const DesignServicesPage = () => {
  const [form, setForm] = useState({ name: "", email: "", room: "", budget: "", details: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.room) {
      toast.error("Please add your name, email, and which room you need help with");
      return;
    }
    setLoading(true);
    const submissionId = crypto.randomUUID();
    const [firstName, ...rest] = form.name.trim().split(" ");
    const message = [
      `Room: ${form.room.trim()}`,
      `Budget: ${form.budget.trim() || "Not specified"}`,
      "",
      form.details.trim(),
    ].join("\n");

    // Loaded here, on submit: imported at the top it made the whole page wait for the client.
    let supabase: Awaited<ReturnType<typeof getSupabase>>;
    try {
      supabase = await getSupabase();
      const { error } = await supabase.from("contact_submissions").insert({
        id: submissionId,
        first_name: firstName,
        last_name: rest.join(" "),
        email: form.email.trim(),
        subject: "Free Design Services request",
        message,
      });
      if (error) throw error;
    } catch {
      toast.error("Couldn't send your request. Please try again.");
      setLoading(false);
      return;
    }

    // Lead captured: show success immediately. Emails are best-effort.
    setSubmitted(true);
    trackLead("design_plan");
    setForm({ name: "", email: "", room: "", budget: "", details: "" });
    setLoading(false);

    Promise.all([
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-notification",
          recipientEmail: "alondrilich@gmail.com",
          idempotencyKey: `design-notify-${submissionId}`,
          templateData: {
            firstName,
            lastName: rest.join(" "),
            email: form.email.trim(),
            subject: "Free Design Services request",
            message,
          },
        },
      }).catch((err) => {
        console.error(`[DesignServices] notification email failed for submission ${submissionId}:`, err);
      }),
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-confirmation",
          recipientEmail: form.email.trim(),
          idempotencyKey: `design-confirm-${submissionId}`,
          templateData: { firstName },
        },
      }).catch((err) => {
        console.error(`[DesignServices] confirmation email failed for submission ${submissionId}:`, err);
      }),
    ]);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <link rel="canonical" href="https://loftloom.com/design-services" />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:url" content="https://loftloom.com/design-services" />
        <meta property="og:type" content="website" />
      </Helmet>
      <AnnouncementBar />
      <Header />
      <StaticRouteBreadcrumbs />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <img src={designServices} alt="Interior design services" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-foreground/40 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="font-display text-4xl md:text-6xl font-semibold text-background tracking-tight mb-4">Free Room Plan</h1>
              <p className="text-background/80 text-lg max-w-md mx-auto mb-6">Get a personalized room plan from LoftLoom — completely free.</p>
              <Button asChild size="lg" className="h-12 px-8 text-sm font-semibold uppercase tracking-wider"><a href="#book">Request a Free Room Plan</a></Button>
            </div>
          </div>
        </section>

        <div className="max-w-3xl mx-auto px-4 pt-16 pb-24 md:py-24">
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="text-center">
              <div className="text-3xl font-display font-semibold text-primary mb-2">1</div>
              <h2 className="text-base font-semibold text-foreground mb-1">Tell us about your space</h2>
              <p className="text-sm text-muted-foreground">Share your room dimensions, style preferences, and budget.</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-display font-semibold text-primary mb-2">2</div>
              <h2 className="text-base font-semibold text-foreground mb-1">Get your design plan</h2>
              <p className="text-sm text-muted-foreground">We create a custom layout with product recommendations.</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-display font-semibold text-primary mb-2">3</div>
              <h2 className="text-base font-semibold text-foreground mb-1">Shop with confidence</h2>
              <p className="text-sm text-muted-foreground">Order your curated selection and enjoy your new space.</p>
            </div>
          </div>

          <div id="book" className="border-t border-border pt-12 scroll-mt-24">
            <h2 className="font-display text-2xl font-semibold text-foreground mb-6 text-center">Get Started</h2>
            {submitted ? (
              <div className="max-w-lg mx-auto border border-border p-8 text-center">
                <CheckCircle aria-hidden="true" className="h-8 w-8 text-primary mx-auto mb-3" />
                <p className="font-semibold text-foreground mb-1">Request received</p>
                <p className="text-sm text-muted-foreground">
                  We'll email you a personalized room plan within 2 business days.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 max-w-lg mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label htmlFor="name" className="editorial-label">Name</Label><Input id="name" className="h-12 bg-background shadow-none" value={form.name} onChange={handleChange} placeholder="Your name" required /></div>
                  <div className="space-y-2"><Label htmlFor="email" className="editorial-label">Email</Label><Input id="email" type="email" className="h-12 bg-background shadow-none" value={form.email} onChange={handleChange} placeholder="you@example.com" required /></div>
                </div>
                <div className="space-y-2"><Label htmlFor="room" className="editorial-label">Which room?</Label><Input id="room" className="h-12 bg-background shadow-none" value={form.room} onChange={handleChange} placeholder="Living room, bedroom, etc." required /></div>
                <div className="space-y-2"><Label htmlFor="budget" className="editorial-label">Budget range</Label><Input id="budget" className="h-12 bg-background shadow-none" value={form.budget} onChange={handleChange} placeholder="e.g. $2,000 - $5,000" /></div>
                <div className="space-y-2"><Label htmlFor="details" className="editorial-label">Tell us more</Label><Textarea id="details" className="bg-background shadow-none" rows={4} value={form.details} onChange={handleChange} placeholder="Describe your style, what you're looking for..." /></div>
                <Button type="submit" disabled={loading} className="w-full h-12 text-sm font-semibold uppercase tracking-wider">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending…</> : "Get My Free Design Plan"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DesignServicesPage;
